import { formatCurrency } from '@crm/shared';
import { Button, Card, CardContent, cn, Spinner } from '@crm/ui';
import { FileSpreadsheet } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExportPdfButton, PageHeader, PrintButton, StatCard } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { toErrorMessage } from '@/modules/auth/utils';
import {
  type IncomeStatement,
  MANAGEMENT_ROUTES,
  type ReportFilters,
  useIncomeStatement,
} from '../api';
import { defaultFilters, ReportFiltersBar } from '../components/report-filters';

interface Line {
  label: string;
  value: number;
  /** Linha de subtotal: fundo e fonte mais fortes. */
  emphasis?: boolean;
  /** Valor que abate - sai com sinal negativo e em vermelho. */
  negative?: boolean;
  hint?: string;
}

function linesOf(dre: IncomeStatement): Line[] {
  return [
    { label: 'Receita bruta de servicos', value: dre.grossRevenue, hint: 'Valor cheio da tabela' },
    { label: 'Descontos', value: dre.discounts, negative: true },
    { label: 'Cupons', value: dre.coupons, negative: true },
    { label: 'Cashback usado', value: dre.cashback, negative: true },
    { label: 'Receita liquida', value: dre.netRevenue, emphasis: true },
    { label: 'Comissoes', value: dre.commissions, negative: true },
    { label: 'Taxas de cartao', value: dre.cardFees, negative: true },
    { label: 'Outras receitas', value: dre.otherIncome },
    { label: 'Despesas', value: dre.expenseTotal, negative: true },
    { label: 'Resultado do periodo', value: dre.result, emphasis: true },
  ];
}

/** DRE: da venda bruta ao resultado, linha a linha. */
export function IncomeStatementPage() {
  useDocumentTitle('DRE - resultado do periodo');

  const navigate = useNavigate();
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters);
  const statement = useIncomeStatement(filters);
  const dre = statement.data;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FileSpreadsheet />}
        title="DRE - resultado do periodo"
        description="Tudo que abate a venda ate sobrar o resultado: desconto, cupom, cashback, comissao, taxa e despesa."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <PrintButton />
            <ExportPdfButton title="DRE - resultado do periodo" />
            <Button variant="outline" onClick={() => navigate(MANAGEMENT_ROUTES.CASH_FLOW)}>
              Ver DFC
            </Button>
          </div>
        }
      />

      <Card variant="flush">
        <CardContent>
          <ReportFiltersBar value={filters} onChange={setFilters} />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
        <StatCard title="Receita liquida" value={formatCurrency(dre?.netRevenue ?? 0)} />
        <StatCard
          title="Custos e despesas"
          value={formatCurrency(
            (dre?.commissions ?? 0) + (dre?.cardFees ?? 0) + (dre?.expenseTotal ?? 0),
          )}
        />
        <StatCard
          title="Resultado"
          value={formatCurrency(dre?.result ?? 0)}
          className={cn(
            'col-span-2 sm:col-span-1',
            dre && dre.result < 0 && 'border-destructive/40',
          )}
        />
      </div>

      {statement.isError ? (
        <p className="text-sm text-destructive">{toErrorMessage(statement.error)}</p>
      ) : null}

      <Card variant="flush">
        <CardContent>
          {statement.isLoading || !dre ? (
            <div className="flex justify-center py-10">
              <Spinner className="h-6 w-6 text-muted-foreground" />
            </div>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {linesOf(dre).map((line) => (
                <li
                  key={line.label}
                  className={cn(
                    'flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0',
                    line.emphasis && 'text-base font-semibold',
                  )}
                >
                  <span className="min-w-0">
                    {line.label}
                    {line.hint ? (
                      <span className="block text-xs font-normal text-muted-foreground">
                        {line.hint}
                      </span>
                    ) : null}
                  </span>
                  <span
                    className={cn(
                      'shrink-0 whitespace-nowrap',
                      line.negative && 'text-red-600 dark:text-red-400',
                      line.emphasis && line.value < 0 && 'text-destructive',
                    )}
                  >
                    {line.negative ? '-' : ''}
                    {formatCurrency(line.value)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {dre && dre.expenses.length > 0 ? (
        <Card variant="flush">
          <CardContent className="space-y-3">
            <p className="text-sm font-medium">Despesas por categoria</p>
            <ul className="divide-y divide-border text-sm">
              {dre.expenses.map((expense) => (
                <li
                  key={expense.name}
                  className="flex items-baseline justify-between gap-3 py-2.5 first:pt-0 last:pb-0"
                >
                  <span className="min-w-0 truncate">{expense.name}</span>
                  <span className="shrink-0 whitespace-nowrap font-medium">
                    {formatCurrency(expense.total)}
                  </span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
